import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, RefreshCw, TrendingDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  getFunnelStats,
  getFunnelSectionStats,
  getPostcodeInvalidReasons,
  getPostJobStepFunnel,
  getTopRatedHealth,
  getSafetyModalAbResults,
} from "@/lib/funnel.functions";

export const Route = createFileRoute("/_authenticated/admin/funnel")({
  head: () => ({ meta: [{ title: "Conversion funnel — Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminFunnelPage,
});

const LABELS: Record<string, string> = {
  hero_cta_click: "Hero CTA click",
  auth_view: "Auth page viewed",
  auth_signup_success: "Sign-up completed",
  post_job_view: "Post-job viewed",
  post_job_submit: "Post-job submitted",
  checkout_view: "Checkout viewed",
  checkout_started: "Checkout started",
  booking_paid: "Booking paid",
};

const SECTION_LABELS: Record<string, string> = {
  trust_strip: "Hero trust strip",
  testimonial: "Testimonials",
  top_rated: "Top rated this week",
  safety_modal: "Safety modal (view → CTA click)",
};

const REASON_LABELS: Record<string, string> = {
  empty: "Postcode blank",
  too_short: "Partial postcode (too short)",
  format: "Doesn't match Greater London postcode format",
  unknown: "Unknown reason",
};

type FunnelData = Awaited<ReturnType<typeof getFunnelStats>>;
type SectionData = Awaited<ReturnType<typeof getFunnelSectionStats>>;
type ReasonData = Awaited<ReturnType<typeof getPostcodeInvalidReasons>>;
type PostJobData = Awaited<ReturnType<typeof getPostJobStepFunnel>>;
type HealthData = Awaited<ReturnType<typeof getTopRatedHealth>>;
type AbData = Awaited<ReturnType<typeof getSafetyModalAbResults>>;

const STALE_AFTER_MIN = 90; // refresh runs hourly — warn if >90 min old.

function pct(n: number) { return `${(n * 100).toFixed(1)}%`; }
function fmtLift(n: number) {
  const sign = n > 0 ? "+" : "";
  return `${sign}${(n * 100).toFixed(1)}%`;
}

function AdminFunnelPage() {
  const fetchStats = useServerFn(getFunnelStats);
  const fetchSections = useServerFn(getFunnelSectionStats);
  const fetchReasons = useServerFn(getPostcodeInvalidReasons);
  const fetchPostJob = useServerFn(getPostJobStepFunnel);
  const fetchHealth = useServerFn(getTopRatedHealth);
  const fetchAb = useServerFn(getSafetyModalAbResults);

  const [days, setDays] = useState(30);
  const [data, setData] = useState<FunnelData | null>(null);
  const [sections, setSections] = useState<SectionData | null>(null);
  const [reasons, setReasons] = useState<ReasonData | null>(null);
  const [postJob, setPostJob] = useState<PostJobData | null>(null);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [ab, setAb] = useState<AbData | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) { setForbidden(true); setLoading(false); return; }
      setLoading(true);
      try {
        const [s, sec, r, pj, h, a] = await Promise.all([
          fetchStats({ data: { days } }),
          fetchSections({ data: { days } }),
          fetchReasons({ data: { days } }),
          fetchPostJob({ data: { days } }),
          fetchHealth(),
          fetchAb({ data: { days: Math.min(days, 30) } }),
        ]);
        if (cancelled) return;
        setData(s); setSections(sec); setReasons(r); setPostJob(pj); setHealth(h); setAb(a);
      } catch { if (!cancelled) setForbidden(true); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [days, tick, fetchStats, fetchSections, fetchReasons, fetchPostJob, fetchHealth, fetchAb]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-5xl flex-1 px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Conversion funnel</h1>
            <p className="mt-1 text-sm text-muted-foreground">Sessions, impressions, clicks and drop-off across the homepage in the last {days} days.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[7, 30, 90].map((d) => (
              <Button key={d} variant={days === d ? "default" : "outline"} size="sm" onClick={() => setDays(d)}>{d}d</Button>
            ))}
            <Button variant="outline" size="sm" onClick={() => setTick((t) => t + 1)}><RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh</Button>
            <Button asChild variant="outline" size="sm"><Link to="/admin/ops">Ops dashboard</Link></Button>
          </div>
        </div>

        {forbidden ? (
          <p className="mt-10 rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground">Admin access required.</p>
        ) : loading || !data ? (
          <div className="mt-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="mt-8 space-y-10">
            {/* Top-rated job health */}
            {health && (
              <section>
                <h2 className="font-display text-xl font-bold">Top-rated helpers job health</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Hourly cron <code className="rounded bg-muted px-1">refresh-top-rated-helpers</code> populates the homepage leaderboard.
                </p>
                <TopRatedHealthCard health={health} />
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <Link to="/admin/ops" className="inline-flex items-center gap-1 text-primary hover:underline">
                    Runbook: webhook & cron incidents <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              </section>
            )}

            {/* Step funnel */}
            <section>
              <h2 className="font-display text-xl font-bold">Step funnel</h2>
              <p className="mt-1 text-sm text-muted-foreground">Unique sessions reaching each step.</p>
              <div className="mt-3 space-y-3">
                {data.steps.map((s, i) => {
                  const prev = i > 0 ? data.steps[i - 1].sessions : s.sessions;
                  const dropoff = prev > 0 ? 1 - s.sessions / prev : 0;
                  return (
                    <div key={s.step} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="font-semibold">{LABELS[s.step] ?? s.step}</p>
                        <div className="text-right">
                          <p className="font-display text-2xl font-bold tabular-nums">{s.sessions.toLocaleString("en-GB")}</p>
                          {i > 0 && dropoff > 0.05 && (
                            <p className="inline-flex items-center gap-1 text-xs text-destructive">
                              <TrendingDown className="h-3 w-3" /> {(dropoff * 100).toFixed(0)}% drop from previous
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-gradient-to-r from-primary to-primary-glow" style={{ width: `${Math.max(s.share * 100, 2)}%` }} />
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">{pct(s.share)} of top of funnel</p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Post-job step funnel + abandonment */}
            {postJob && (
              <section>
                <h2 className="font-display text-xl font-bold">Post-job flow — step conversion &amp; abandonment</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Unique sessions reaching each post-job step, with drop-off from the previous step and where partial drafts were abandoned.
                </p>
                <div className="mt-3 space-y-3">
                  {postJob.steps.map((s, i) => (
                    <div key={s.step} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="font-semibold">{s.label}</p>
                        <div className="text-right">
                          <p className="font-display text-2xl font-bold tabular-nums">{s.sessions.toLocaleString("en-GB")}</p>
                          {i > 0 && s.dropoff_from_previous > 0.05 && (
                            <p className="inline-flex items-center gap-1 text-xs text-destructive">
                              <TrendingDown className="h-3 w-3" /> {(s.dropoff_from_previous * 100).toFixed(0)}% drop from previous
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-gradient-to-r from-primary to-primary-glow" style={{ width: `${Math.max(s.share * 100, 2)}%` }} />
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">{pct(s.share)} of opened-post-job sessions</p>
                    </div>
                  ))}
                </div>

                <div className="mt-4 rounded-2xl border border-border bg-card p-5 shadow-card">
                  <div className="flex items-baseline justify-between">
                    <p className="font-semibold">Abandonment by step</p>
                    <p className="text-sm tabular-nums text-muted-foreground">{postJob.abandon_total.toLocaleString("en-GB")} abandons</p>
                  </div>
                  {postJob.abandon.length === 0 ? (
                    <p className="mt-3 text-sm text-muted-foreground">No tracked abandonment events in window.</p>
                  ) : (
                    <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                      {postJob.abandon.map((a) => (
                        <li key={a.at} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-sm">
                          <span className="font-medium">{a.at}</span>
                          <span className="tabular-nums text-muted-foreground">{a.count.toLocaleString("en-GB")}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            )}

            {/* Section impressions/clicks */}
            {sections && (
              <section>
                <h2 className="font-display text-xl font-bold">Homepage sections — impressions vs clicks</h2>
                <p className="mt-1 text-sm text-muted-foreground">CTR and drop-off per instrumented block. High impressions + low CTR = conversion leak.</p>
                <div className="mt-3 overflow-hidden rounded-2xl border border-border bg-card shadow-card">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Section</th>
                        <th className="px-4 py-3 text-right">Impr.</th>
                        <th className="px-4 py-3 text-right">Impr. sessions</th>
                        <th className="px-4 py-3 text-right">Clicks</th>
                        <th className="px-4 py-3 text-right">Click sessions</th>
                        <th className="px-4 py-3 text-right">CTR</th>
                        <th className="px-4 py-3 text-right">Drop-off</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sections.sections.map((s) => (
                        <tr key={s.section} className="border-t border-border">
                          <td className="px-4 py-3 font-medium">{SECTION_LABELS[s.section] ?? s.section}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{s.impressions.toLocaleString("en-GB")}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{s.impression_sessions.toLocaleString("en-GB")}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{s.clicks.toLocaleString("en-GB")}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{s.click_sessions.toLocaleString("en-GB")}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{pct(s.ctr_sessions)}</td>
                          <td className={`px-4 py-3 text-right tabular-nums ${s.dropoff_sessions > 0.8 ? "text-destructive" : ""}`}>{pct(s.dropoff_sessions)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Postcode invalid reasons */}
            {reasons && (
              <section>
                <h2 className="font-display text-xl font-bold">Search flow — postcode validation failures</h2>
                <p className="mt-1 text-sm text-muted-foreground">{reasons.total.toLocaleString("en-GB")} invalid attempts. Use this to tune error microcopy.</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {reasons.reasons.length === 0 ? (
                    <p className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">No invalid postcode events in window.</p>
                  ) : reasons.reasons.map((r) => {
                    const share = reasons.total > 0 ? r.count / reasons.total : 0;
                    return (
                      <div key={r.reason} className="rounded-2xl border border-border bg-card p-4 shadow-card">
                        <div className="flex items-baseline justify-between">
                          <p className="font-semibold">{REASON_LABELS[r.reason] ?? r.reason}</p>
                          <p className="font-display text-xl font-bold tabular-nums">{r.count.toLocaleString("en-GB")}</p>
                        </div>
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full bg-amber-500" style={{ width: `${Math.max(share * 100, 2)}%` }} />
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{pct(share)} of failures · {r.sessions.toLocaleString("en-GB")} sessions</p>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* A/B test */}
            {ab && (
              <section>
                <h2 className="font-display text-xl font-bold">A/B test — Safety modal copy &amp; CTA placement</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Experiment <code className="rounded bg-muted px-1">safety_modal_copy_v1</code>. Variant B uses outcome-led copy and moves the primary CTA to the top.
                </p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <VariantCard name="A — Control" v={ab.variants.A} />
                  <VariantCard name="B — Treatment" v={ab.variants.B} />
                </div>
                <div className="mt-3 rounded-2xl border border-border bg-card p-4 shadow-card">
                  <p className="font-semibold">Uplift (B vs A)</p>
                  <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Trust-strip click rate</p>
                      <p className={`mt-1 font-display text-2xl font-bold tabular-nums ${ab.uplift.trust_click_rate > 0 ? "text-emerald-600" : ab.uplift.trust_click_rate < 0 ? "text-destructive" : ""}`}>
                        {fmtLift(ab.uplift.trust_click_rate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Helper / customer signup rate</p>
                      <p className={`mt-1 font-display text-2xl font-bold tabular-nums ${ab.uplift.signup_rate > 0 ? "text-emerald-600" : ab.uplift.signup_rate < 0 ? "text-destructive" : ""}`}>
                        {fmtLift(ab.uplift.signup_rate)}
                      </p>
                    </div>
                  </div>
                  {ab.variants.unassigned.exposed > 0 && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      {ab.variants.unassigned.exposed.toLocaleString("en-GB")} sessions without an assigned variant (cookie consent off / pre-bucket events) — excluded from uplift.
                    </p>
                  )}
                </div>
              </section>
            )}

            <p className="text-xs text-muted-foreground">{data.total_events.toLocaleString("en-GB")} raw events in step-funnel window.</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function TopRatedHealthCard({ health }: { health: HealthData }) {
  const age = health.cache_age_minutes ?? null;
  const stale = age != null && age > STALE_AFTER_MIN;
  const lastFailed = health.last_run_status != null && health.last_run_status !== "succeeded";
  const failures = health.failures_24h ?? 0;
  const alert = stale || lastFailed || failures > 0 || (health.cache_count ?? 0) === 0;

  return (
    <div className={`mt-3 rounded-2xl border p-5 shadow-card ${alert ? "border-destructive/40 bg-destructive/5" : "border-border bg-card"}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${alert ? "bg-destructive/15 text-destructive" : "bg-emerald-500/10 text-emerald-600"}`}>
            {alert ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
          </span>
          <div>
            <p className="font-semibold">{alert ? "Attention required" : "Healthy"}</p>
            <p className="text-sm text-muted-foreground">
              {health.cache_count ?? 0} helpers cached · {age == null ? "never refreshed" : `refreshed ${Math.round(age)} min ago`}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Last run: <span className="font-medium">{health.last_run_status ?? "unknown"}</span>
              {health.last_run_start ? ` · ${new Date(health.last_run_start).toLocaleString("en-GB")}` : ""}
              {failures > 0 ? ` · ${failures} failure(s) in 24h` : ""}
            </p>
            {health.last_run_message && lastFailed && (
              <p className="mt-2 max-w-xl rounded bg-muted/70 p-2 text-xs text-muted-foreground">{health.last_run_message}</p>
            )}
          </div>
        </div>
      </div>

      {alert && (
        <div className="mt-4 rounded-xl bg-card/50 p-3 text-xs text-muted-foreground">
          <p className="font-semibold text-foreground">Runbook</p>
          <ol className="mt-1 list-decimal space-y-1 pl-5">
            <li>Run the refresh manually: <code className="rounded bg-muted px-1">SELECT public.refresh_top_rated_helpers();</code></li>
            <li>If it errors, check <code className="rounded bg-muted px-1">cron.job_run_details</code> for the last <code>refresh-top-rated-helpers</code> entry.</li>
            <li>Verify <code>helper_profiles</code> has rows matching <code>avg_rating ≥ 4.5</code> and <code>rating_count ≥ 3</code>.</li>
            <li>Re-enable schedule if disabled: see migration <code>20260616012807</code>.</li>
          </ol>
        </div>
      )}
    </div>
  );
}

function VariantCard({ name, v }: { name: string; v: AbData["variants"]["A"] }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-card">
      <p className="font-semibold">{name}</p>
      <div className="mt-3 grid grid-cols-2 gap-y-1 text-sm">
        <span className="text-muted-foreground">Exposed</span><span className="text-right tabular-nums">{v.exposed.toLocaleString("en-GB")}</span>
        <span className="text-muted-foreground">Trust clicks</span><span className="text-right tabular-nums">{v.trust_clicks.toLocaleString("en-GB")}</span>
        <span className="text-muted-foreground">Modal views</span><span className="text-right tabular-nums">{v.modal_views.toLocaleString("en-GB")}</span>
        <span className="text-muted-foreground">Modal CTA clicks</span><span className="text-right tabular-nums">{v.cta_clicks.toLocaleString("en-GB")}</span>
        <span className="text-muted-foreground">Signups</span><span className="text-right tabular-nums">{v.signups.toLocaleString("en-GB")}</span>
        <span className="text-muted-foreground">Click rate</span><span className="text-right tabular-nums">{pct(v.trust_click_rate)}</span>
        <span className="text-muted-foreground">Signup rate</span><span className="text-right tabular-nums">{pct(v.signup_rate)}</span>
      </div>
    </div>
  );
}
